import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Alert from '../../../../core/components/Alert.tsx';
import DropArea from '../../../../core/components/DropArea.tsx';
import FormField from '../../../../core/components/FormField.tsx';
import Switch from '../../../../core/components/Switch.tsx';
import { useToaster } from '../../../../core/components/Toast.tsx';
import { KNOWLEDGE_FILE_ACCEPT, KNOWLEDGE_SUPPORTED_EXTENSIONS_LABEL } from '../../constants.ts';
import InstructionSections from '../knowledge/InstructionSections.tsx';
import KnowledgeFileList from '../knowledge/KnowledgeFileList.tsx';
import { KnowledgeUrlItems, KnowledgeUrlStatus, UrlSyncedFile } from '../knowledge/KnowledgeUrlItems.tsx';

const AgentKnowledgeFormFields = ({ form, agent }: { form: any; agent?: any }) => {
    const { t } = useTranslation('assistants', { keyPrefix: 'agentConfig' });
    const { publishToast } = useToaster();

    const formErrors = form.formState.errors;
    const files = form.watch('files') || [];

    const urlStatuses = new Map<string, KnowledgeUrlStatus>(
        (agent?.knowledgeUrlStatuses || []).map((status: KnowledgeUrlStatus) => [status.url, status])
    );
    const urlSyncedFiles = new Map<string, UrlSyncedFile>(
        (agent?.knowledgeFiles || [])
            .filter((file: any) => file.sourceType === 'url' && file.sourceItemId)
            .map((file: any) => [file.sourceItemId, { fileName: file.fileName, fileUrl: file.fileUrl }])
    );
    const savedUrls = new Set<string>(agent?.knowledgeUrls || []);

    const handleFilesChange = (newFiles: File[]) => {
        const currentFiles = form.getValues('files') || [];
        form.setValue('files', [...currentFiles, ...newFiles]);
    };

    const handleDeleteFile = (fileToDelete: File | any) => {
        const currentFiles = form.getValues('files') || [];
        form.setValue(
            'files',
            currentFiles.filter((f: File | any) => f !== fileToDelete)
        );
    };

    const handleDropRejected = (fileRejections: any[]) => {
        const rejectedNames = fileRejections.map((r) => r.file.name).join(', ');
        publishToast({
            description: t('form.unsupportedFileType', {
                files: rejectedNames,
                supportedTypes: KNOWLEDGE_SUPPORTED_EXTENSIONS_LABEL,
            }),
            status: 'error',
        });
    };

    return (
        <div className="flex flex-col gap-4">
            <Controller
                name="instructions"
                control={form.control}
                render={({ field }) => (
                    <InstructionSections label={t('form.instructions')} value={field.value} onChange={field.onChange} />
                )}
            />

            <FormField
                label={t('form.knowledgeFiles')}
                helpText={t('form.knowledgeFilesDescription', { supportedTypes: KNOWLEDGE_SUPPORTED_EXTENSIONS_LABEL })}
            >
                <div className="flex flex-col gap-2">
                    <DropArea
                        placeholder={t('form.dropFilesPlaceholder')}
                        placeholderActive={t('form.dropFilesActive')}
                        onFilesChange={handleFilesChange}
                        onDropRejected={handleDropRejected}
                        accept={KNOWLEDGE_FILE_ACCEPT}
                        files={[]}
                    />
                    {files.length > 0 && <KnowledgeFileList files={files} onDelete={handleDeleteFile} />}
                    <Controller
                        name="knowledgeUrls"
                        control={form.control}
                        render={({ field }) => (
                            <KnowledgeUrlItems
                                urls={field.value || []}
                                onChange={field.onChange}
                                savedUrls={savedUrls}
                                statuses={urlStatuses}
                                syncedFiles={urlSyncedFiles}
                            />
                        )}
                    />
                </div>
            </FormField>

            <Controller
                name="attachmentsEnabled"
                control={form.control}
                render={({ field }) => (
                    <div className="rounded-xl flex flex-col gap-2 p-2 border border-line-weak border-solid bg-surface-weak shadow shadow-black/5 transition-colors">
                        <label className="flex flex-col cursor-pointer hover:border-line-strong hover:bg-surface-medium group transition-colors rounded-md">
                            <div className="flex justify-between items-center p-2 pb-0">
                                <h3 className="text-sm font-medium text-content-strong">
                                    {t('toolProviders.attachments.name')}
                                </h3>
                                <Switch value={field.value} onValueChange={field.onChange} />
                            </div>
                            <div className="text-sm text-content-medium px-2 pb-2">
                                {t('toolProviders.attachments.description')}
                            </div>
                        </label>
                        <Alert severity="info">{t('toolProviders.attachments.requiresConsent')}</Alert>
                    </div>
                )}
            />
        </div>
    );
};

export default AgentKnowledgeFormFields;
